(() => {
  "use strict";

  const SECTION_SELECTOR =
    "[data-digi-club-launches]";

  const CARD_SELECTOR =
    "[data-digi-club-launches-card]";

  const REVEAL_SELECTOR =
    "[data-digi-club-launches-reveal]";

  const instances = new WeakMap();

  class DigiClubLaunches {
    constructor(section) {
      this.section = section;

      this.cards = Array.from(
        section.querySelectorAll(
          CARD_SELECTOR
        )
      );

      this.revealElements = Array.from(
        section.querySelectorAll(
          REVEAL_SELECTOR
        )
      );

      this.revealObserver = null;

      this.handlePointerEnter =
        this.handlePointerEnter.bind(this);

      this.handlePointerLeave =
        this.handlePointerLeave.bind(this);

      this.handleFocusIn =
        this.handleFocusIn.bind(this);

      this.handleFocusOut =
        this.handleFocusOut.bind(this);

      this.init();
    }

    init() {
      this.bindEvents();
      this.setupReveal();
    }

    bindEvents() {
      this.cards.forEach((card) => {
        card.addEventListener(
          "pointerenter",
          this.handlePointerEnter
        );

        card.addEventListener(
          "pointerleave",
          this.handlePointerLeave
        );

        card.addEventListener(
          "focusin",
          this.handleFocusIn
        );

        card.addEventListener(
          "focusout",
          this.handleFocusOut
        );
      });
    }

    unbindEvents() {
      this.cards.forEach((card) => {
        card.removeEventListener(
          "pointerenter",
          this.handlePointerEnter
        );

        card.removeEventListener(
          "pointerleave",
          this.handlePointerLeave
        );

        card.removeEventListener(
          "focusin",
          this.handleFocusIn
        );

        card.removeEventListener(
          "focusout",
          this.handleFocusOut
        );
      });
    }

    handlePointerEnter(event) {
      this.setActiveCard(
        event.currentTarget
      );
    }

    handlePointerLeave(event) {
      event.currentTarget.classList.remove(
        "is-active"
      );
    }

    handleFocusIn(event) {
      this.setActiveCard(
        event.currentTarget
      );
    }

    handleFocusOut(event) {
      const card =
        event.currentTarget;

      if (
        card.contains(
          event.relatedTarget
        )
      ) {
        return;
      }

      card.classList.remove(
        "is-active"
      );
    }

    setActiveCard(activeCard) {
      this.cards.forEach((card) => {
        card.classList.toggle(
          "is-active",
          card === activeCard
        );
      });
    }

    setupReveal() {
      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      const elements = [
        ...this.revealElements,
        ...this.cards
      ];

      if (!elements.length) {
        return;
      }

      if (
        reducedMotion ||
        !("IntersectionObserver" in window)
      ) {
        this.showAll();
        return;
      }

      this.revealObserver =
        new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                return;
              }

              const element =
                entry.target;

              let delay = 0;

              if (
                element.matches(
                  CARD_SELECTOR
                )
              ) {
                const index =
                  Number(
                    element.dataset.cardIndex
                  ) || 1;

                delay =
                  Math.min(
                    (index - 1) * 70,
                    420
                  );
              }

              window.setTimeout(() => {
                if (!element.isConnected) {
                  return;
                }

                element.classList.add(
                  "is-visible"
                );
              }, delay);

              observer.unobserve(
                element
              );
            });
          },
          {
            threshold: 0.15,
            rootMargin:
              "0px 0px -8% 0px"
          }
        );

      elements.forEach((element) => {
        this.revealObserver.observe(
          element
        );
      });
    }

    showAll() {
      [
        ...this.revealElements,
        ...this.cards
      ].forEach((element) => {
        element.classList.add(
          "is-visible"
        );
      });
    }

    selectBlock(blockId) {
      if (!blockId) {
        return;
      }

      const card =
        this.cards.find(
          (item) =>
            String(
              item.dataset.blockId
            ) ===
            String(blockId)
        );

      if (!card) {
        return;
      }

      this.clearSelection();

      card.classList.add(
        "is-selected",
        "is-visible"
      );

      this.scrollToCard(card);
    }

    deselectBlock(blockId) {
      if (!blockId) {
        return;
      }

      const card =
        this.cards.find(
          (item) =>
            String(
              item.dataset.blockId
            ) ===
            String(blockId)
        );

      card?.classList.remove(
        "is-selected"
      );
    }

    clearSelection() {
      this.cards.forEach((card) => {
        card.classList.remove(
          "is-selected"
        );
      });
    }

    scrollToCard(card) {
      const reducedMotion =
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

      card.scrollIntoView({
        behavior:
          reducedMotion
            ? "auto"
            : "smooth",
        block: "center",
        inline: "nearest"
      });
    }

    destroy() {
      this.unbindEvents();

      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      this.cards.forEach((card) => {
        card.classList.remove(
          "is-active",
          "is-selected"
        );
      });

      this.cards = [];
      this.revealElements = [];
      this.section = null;
    }
  }

  function initializeSection(section) {
    if (
      !section ||
      instances.has(section)
    ) {
      return;
    }

    instances.set(
      section,
      new DigiClubLaunches(section)
    );
  }

  function initialize(root) {
    if (!root) {
      return;
    }

    if (
      root.matches?.(
        SECTION_SELECTOR
      )
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach(
        initializeSection
      );
  }

  function unload(root) {
    if (!root) {
      return;
    }

    const sections = [];

    if (
      root.matches?.(
        SECTION_SELECTOR
      )
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(
        SECTION_SELECTOR
      )
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      const instance =
        instances.get(section);

      if (!instance) {
        return;
      }

      instance.destroy();

      instances.delete(section);
    });
  }

  function getSection(event) {
    return (
      event.target?.closest?.(
        SECTION_SELECTOR
      ) || null
    );
  }

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const section =
        getSection(event);

      if (!section) {
        return;
      }

      instances
        .get(section)
        ?.selectBlock(
          event.detail.blockId
        );
    }
  );

  document.addEventListener(
    "shopify:block:deselect",
    (event) => {
      const section =
        getSection(event);

      if (!section) {
        return;
      }

      instances
        .get(section)
        ?.deselectBlock(
          event.detail.blockId
        );
    }
  );

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialize(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      unload(event.target);
    }
  );

  function ready() {
    initialize(document);
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      ready,
      {
        once: true
      }
    );
  } else {
    ready();
  }
})();